import React, { useState } from "react";
import { Building2, ChevronDown } from "lucide-react";
import QuickViewTableHeader from "./components/QuickViewTableHeader";
import QuickViewRow from "./components/QuickViewRow";

/**
 * Renders the results table: questions grouped into collapsible module
 * sections, with one value column per period.
 *
 * @param {Object} props
 * @param {Object<string, Array<Object>>} props.groupedModules - Module -> questions.
 * @param {Array<{key: string, label: string}>} props.periods - Ordered period columns.
 * @param {Object<string, Object>} props.quickViewMap - questionId -> bucket key -> cell.
 * @param {Object<string, string>} props.unitSettingsMap - questionId -> unit fallback.
 * @param {boolean} [props.loadingValues=false] - Values are still in flight; the
 *   questions render immediately and the period columns fill in on arrival.
 * @returns {JSX.Element} The table card.
 */
const QuickViewTable = ({
  groupedModules,
  periods,
  quickViewMap,
  unitSettingsMap,
  loadingValues = false,
}) => {
  const moduleNames = Object.keys(groupedModules);
  const [collapsedModules, setCollapsedModules] = useState(() => new Set());

  /**
   * Collapses or expands a module section.
   *
   * @param {string} moduleName - The module to toggle.
   * @returns {void}
   */
  const toggleModule = (moduleName) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleName)) next.delete(moduleName);
      else next.add(moduleName);
      return next;
    });
  };

  return (
    <div className="qv-card qv-table-card">
      {loadingValues && (
        <div className="qv-values-loading" role="status" aria-live="polite">
          <span className="qv-values-loading__bar" aria-hidden="true" />
          <span className="qv-values-loading__text">Loading values…</span>
        </div>
      )}
      <div className="qv-table-scroll">
        <div className="qv-table-wrap">
          <table className="qv-table">
            <QuickViewTableHeader periods={periods} />
            <tbody>
              {moduleNames.length === 0 && (
                <tr>
                  <td colSpan={periods.length + 2} className="qv-empty">
                    No questions found for the selected filters.
                  </td>
                </tr>
              )}

              {moduleNames.map((moduleName) => {
                const isCollapsed = collapsedModules.has(moduleName);
                const questions = groupedModules[moduleName];

                return (
                  <React.Fragment key={moduleName}>
                    <tr
                      className="qv-module-row"
                      onClick={() => toggleModule(moduleName)}
                      title={isCollapsed ? "Expand module" : "Collapse module"}
                    >
                      <td colSpan={periods.length + 2} className="qv-module-cell">
                        <div className="qv-module-cell__inner">
                          <ChevronDown
                            className={`qv-chevron ${isCollapsed ? "qv-chevron--collapsed" : ""}`}
                            size={16}
                            aria-hidden="true"
                          />
                          <Building2 size={15} aria-hidden="true" />
                          {moduleName}
                          <span className="qv-module-count">({questions.length})</span>
                        </div>
                      </td>
                    </tr>

                    {!isCollapsed &&
                      questions.map((question, index) => (
                        <QuickViewRow
                          key={question.questionId}
                          question={question}
                          index={index}
                          periods={periods}
                          quickViewMap={quickViewMap}
                          unitSettingsMap={unitSettingsMap}
                        />
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuickViewTable;
